import { z } from "zod";
import { PAYMENT_DECISIONS, PAYMENT_METHODS, REVOKE_PAYMENT_ACTIONS, } from "@membentry/shared-types";
import { dateOnlyStringSchema } from "./date";
const REASON_MAX_LENGTH = 500;
const UUID_V4_LOWERCASE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const paymentDecisionSchema = z.enum(PAYMENT_DECISIONS);
/** Canonical Idempotency-Key: lowercase UUID v4. */
export const idempotencyKeySchema = z
    .string()
    .regex(UUID_V4_LOWERCASE, "Idempotency key must be a lowercase UUID v4");
const reasonSchema = z
    .string()
    .trim()
    .min(1, "Reason is required")
    .max(REASON_MAX_LENGTH);
const paymentDateSchema = z.union([dateOnlyStringSchema, z.iso.datetime({ offset: true })]);
/** PaidNow needs a method; PayLater must not carry money details. Absent decision keeps legacy behaviour. */
export function refineMembershipPaymentDecision(value, context) {
    if (value.paymentDecision === "PaidNow" && !value.paymentMethod) {
        context.addIssue({
            code: "custom",
            path: ["paymentMethod"],
            message: "Payment method is required for a paid membership",
        });
    }
    if (value.paymentDecision === "PayLater" && (value.paymentMethod || value.paidAt)) {
        context.addIssue({
            code: "custom",
            path: ["paymentDecision"],
            message: "A later payment has no method or paid date",
        });
    }
}
export const membershipPaymentDecisionFields = {
    paymentDecision: paymentDecisionSchema.optional(),
    paymentMethod: paymentMethodSchema.optional(),
    paidAt: paymentDateSchema.optional(),
};
export const markPaymentPaidSchema = z.object({
    method: paymentMethodSchema,
    paidAt: paymentDateSchema,
    externalPaymentId: z.string().max(128).optional(),
    allowSecondPaid: z.boolean().optional(),
    overrideReason: reasonSchema.optional(),
}).superRefine((value, context) => {
    if (value.allowSecondPaid && !value.overrideReason) {
        context.addIssue({
            code: "custom",
            path: ["overrideReason"],
            message: "A reason is required to record a second paid payment",
        });
    }
});
export const markPaymentUnpaidSchema = z.object({ reason: reasonSchema });
export const refundPaymentSchema = z.object({
    reason: reasonSchema,
    refundedAt: paymentDateSchema,
});
export const voidPaymentSchema = z.object({ reason: reasonSchema });
/** KeepPayment needs nothing more; Refund needs reason and the day the money was returned; Void needs a reason. */
export const revokePaymentDecisionSchema = z
    .object({
    paymentId: z.string().uuid(),
    action: z.enum(REVOKE_PAYMENT_ACTIONS),
    reason: reasonSchema.optional(),
    refundedAt: paymentDateSchema.optional(),
})
    .superRefine((value, context) => {
    if (value.action !== "KeepPayment" && !value.reason) {
        context.addIssue({ code: "custom", path: ["reason"], message: "Reason is required" });
    }
    if (value.action === "Refund" && !value.refundedAt) {
        context.addIssue({
            code: "custom",
            path: ["refundedAt"],
            message: "Refund date is required",
        });
    }
});
export const changePlanSchema = z
    .object({
    clubId: z.number().min(1, "Club ID is required"),
    userId: z.string().min(1, "Member ID is required"),
    subscriptionId: z.number().min(1, "Subscription ID is required"),
    newMembershipPlanId: z.number().min(1, "Plan is required"),
    startDate: dateOnlyStringSchema.optional(),
    difference: z
        .object({
        decision: paymentDecisionSchema,
        method: paymentMethodSchema.optional(),
        paidAt: paymentDateSchema.optional(),
    })
        .optional(),
})
    .superRefine((value, context) => {
    if (value.difference?.decision === "PaidNow" && !value.difference.method) {
        context.addIssue({
            code: "custom",
            path: ["difference", "method"],
            message: "Payment method is required for a paid difference",
        });
    }
});
